import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { Grid, Message, Segment, SegmentGroup, Button, Icon } from 'semantic-ui-react';
import { Label, TitleSegment } from '@britishcouncil/react-admin';
import { Timestamp } from '@britishcouncil/common-core';

import {
  FormActions,
  RHFormDatePicker,
  RHFormSelect,
  RHFormTextarea,
  useFormContext,
} from 'components/form';
import { formatDate } from 'utils';
import { NextMonitoringDateForm } from '../../common';
import {
  useCalculateNextStageQuery,
  useCalculateNextMonitoringDateQuery,
  useCheckCanEditCustomNextMonitoringDateQuery,
} from 'store/monitoring';
import { OdpMonitoringFormData } from './odpMonitoringValidation';
import {
  ODP_MONITORING_REASON_OPTIONS,
  ODP_MONITORING_RESULT_OPTIONS,
  ODP_MONITORING_STAGE_OPTIONS,
} from './odpMonitoringOptions';

interface OdpMonitoringFormProps {
  deleted?: { deletedOn?: string | null; deletedBy?: string | null };
  loading?: boolean;
  examinerId: number;
  monitoringId?: number;
  onAddFollowUpClick?: () => void;
  showAddFollowUpButton?: boolean;
}

export const OdpMonitoringForm = ({
  deleted,
  loading,
  examinerId,
  monitoringId,
  onAddFollowUpClick,
  showAddFollowUpButton,
}: OdpMonitoringFormProps) => {
  const isNewMonitoring = !monitoringId;
  const {
    mode,
    formMethods: { control, setValue, trigger },
  } = useFormContext<OdpMonitoringFormData>();
  const [shouldCalculate, setShouldCalculate] = useState(isNewMonitoring);
  const [shouldFetchCalculatedOptions, setShouldFetchCalculatedOptions] =
    useState(!isNewMonitoring);

  // Watch form fields
  const [date, result] = useWatch({ control, name: ['date', 'result'] });

  const { data: canEditCustomNextMonitoringDate, isFetching: isCheckingEditPermission } =
    useCheckCanEditCustomNextMonitoringDateQuery(
      { monitoringId, examinerId, monitoringDate: date },
      { skip: !date }
    );

  const { data: calculatedStageData, isFetching: isCalculatingStage } = useCalculateNextStageQuery(
    { examinerId, date: date },
    { skip: !isNewMonitoring || !date }
  );

  const { data: _calculatedNextMonitoringDateOptions, isFetching: isCalculatingNextDate } =
    useCalculateNextMonitoringDateQuery(
      { date, result },
      { skip: !shouldFetchCalculatedOptions || !date || !result }
    );

  const resetNextMonitoringDateSelection = () => {
    setShouldFetchCalculatedOptions(false);
    setShouldCalculate(true);
    setValue('nextMonitoringDateType', 'system', { shouldValidate: true });
    setValue('customNextMonitoringDate', null, { shouldValidate: true });
  };

  // Auto-set calculated stage
  useEffect(() => {
    if (calculatedStageData?.stage !== undefined) {
      setValue('stage', calculatedStageData.stage, { shouldValidate: true });
    }
  }, [calculatedStageData, setValue]);

  const calculatedNextMonitoringDateOptions =
    (shouldFetchCalculatedOptions && !isCalculatingStage) || mode == 'read'
      ? _calculatedNextMonitoringDateOptions
      : null;

  const canShowNextMonitoringDateSection = canEditCustomNextMonitoringDate === true;

  const isLoading = loading || isCalculatingStage || isCheckingEditPermission || loading;
  const showNextMonitoringDateSection =
    canShowNextMonitoringDateSection && calculatedNextMonitoringDateOptions && !isLoading;

  const showCalculateButton =
    mode !== 'read' &&
    canShowNextMonitoringDateSection &&
    shouldCalculate &&
    !calculatedNextMonitoringDateOptions;

  return (
    <>
      <SegmentGroup>
        <TitleSegment
          title={<>Monitoring Details {deleted?.deletedBy && <Label color="red">Deleted</Label>}</>}
          rightSection={
            <>
              {showAddFollowUpButton && (
                <Button size="tiny" color="green" onClick={onAddFollowUpClick}>
                  <Icon name="plus" /> Add follow up
                </Button>
              )}
            </>
          }
        />
        <Segment loading={isLoading}>
          <Grid columns={4} stackable>
            <Grid.Column width={4}>
              <RHFormDatePicker
                name="date"
                label="Monitoring date"
                onChange={resetNextMonitoringDateSelection}
                control={control}
                required
                toDate={Timestamp.now()}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <RHFormSelect
                name="reason"
                label="Reason for monitoring"
                onChange={resetNextMonitoringDateSelection}
                control={control}
                options={ODP_MONITORING_REASON_OPTIONS}
                required
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <RHFormSelect
                name="result"
                label="Monitoring result"
                onChange={resetNextMonitoringDateSelection}
                control={control}
                options={ODP_MONITORING_RESULT_OPTIONS}
                required
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <RHFormSelect
                name="stage"
                label="Monitoring stage"
                onChange={resetNextMonitoringDateSelection}
                control={control}
                options={ODP_MONITORING_STAGE_OPTIONS}
                required
                loading={isCalculatingStage}
                disabled={isCalculatingStage}
              />
            </Grid.Column>
            <Grid.Column width={16}>
              <RHFormTextarea name="comment" label="Comment" control={control} rows={3} />
            </Grid.Column>
          </Grid>

          {deleted?.deletedBy && (
            <Message warning>
              <Message.Content>
                This ODP monitoring was deleted by {deleted.deletedBy} on{' '}
                {formatDate(deleted.deletedOn)}.
              </Message.Content>
            </Message>
          )}
        </Segment>

        {showNextMonitoringDateSection && (
          <NextMonitoringDateForm nextDateOptions={calculatedNextMonitoringDateOptions} />
        )}
      </SegmentGroup>
      <FormActions
        customSubmitBtn={
          showCalculateButton ? (
            <Button
              type="button"
              primary
              icon="calculator"
              content="Calculate"
              onClick={async () => {
                const isValid = await trigger();
                if (isValid) {
                  setShouldFetchCalculatedOptions(true);
                }
              }}
              loading={isCalculatingNextDate}
              disabled={isCalculatingNextDate}
            />
          ) : null
        }
      />
    </>
  );
};
